import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Space, Table, Button, Input, Popconfirm, Spin } from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import type { InputRef } from "antd";
import type { FilterConfirmProps } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import {
  getAllParticipants,
  deleteParticipant,
} from "../../services/ParticipantService";
import { getAllEvents } from "../../services/EventService";
import "./ParticipantCompanyListPage.scss";
import { Participant } from "../../models/Participant";
import { PdfPreview } from "../../components/PdfPreview/PdfPreview";
import { getParticipantListReportTemplate } from "../../document-templates/ParticipantListReportTemplate";
import { cloneDeep } from "lodash";
import { RegistrationStatus } from "../../models/Registration";
import { activeUser } from "../../services/AuthServices";
import { formatDate, formatTime } from "../../core/Utility";
import { RoleType } from "../../models/Role";
import { Toast } from "primereact/toast";
import CustomButton from "../../components/AccommodationData/CustomButton/CustomButton";

const ParticipantCompanyListPage = () => {
  const navigate = useNavigate();
  const { setBreadcrumb } = useApplicationContext();
  const [typeOptions, setTypeOptions] = useState<
    { text: string; value: string }[]
  >([]);

  const [companyParticipantList, setCompanyParticipantList] = useState<any[]>(
    []
  );
  const [filteredParticipantList, setFilteredParticipantList] = useState<
    Participant[]
  >([]);
  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const [activeSpinner, setActiveSpinner] = useState<boolean>(false);
  const [allEventOptions, setAllEventOptions] = useState<
    { text: string; value: string }[]
  >([]);
  const [yearOptions, setYearOptions] = useState<
    { text: string; value: string }[]
  >([]);
  const [deleteId, setDeleteId] = useState<string>("");
  const [openExportButtons, setOpenExportButtons] = useState<boolean>(false);
  const searchInput = useRef<InputRef>(null);

  const [pdfData, setPdfData] = useState<any>(
    getParticipantListReportTemplate(filteredParticipantList)
  );

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);
  const toast = useRef<any>(null);
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [paginationNumber, setPaginationNumber] = useState<any>(30);

  const statusOptions = [
    { text: "Uplaćeno", value: RegistrationStatus.Paid },
    { text: "Rezervisano", value: RegistrationStatus.Reserved },
    { text: "Odobreno", value: RegistrationStatus.Approved },
  ];

  useEffect(() => {
    setBreadcrumb(["Učesnici", "Lista pravnih lica"]);
    fetchAllParticipants();
  }, []);

  const fetchAllParticipants = () => {
    setActiveSpinner(true);
    let copyOfNewParticipants: Participant[] = [];

    getAllParticipants()
      .then((response) => {
        response.data.result.map((item: any) => {
          let oneParticipant = cloneDeep(item);
          oneParticipant.companyName =
            oneParticipant?.registration?.registrationCompanyName ?? null;
          copyOfNewParticipants.push(oneParticipant);
        });

        const participantWithRegistrationCompany =
          copyOfNewParticipants?.filter(
            (el: any) => el?.registration?.registrationCompanyName !== null
          );

        setCompanyParticipantList(participantWithRegistrationCompany);
        setFilteredParticipantList(participantWithRegistrationCompany);

        let newTypeOptions: { text: string; value: string }[] = [];
        response.data.result?.map((item: any) => {
          newTypeOptions.push({ text: item.type, value: item.type });
        });
        newTypeOptions = newTypeOptions.filter(
          (
            value,
            index,
            self //remove duplicate
          ) => index === self.findIndex((t) => t.text === value.text)
        );
        setTypeOptions(newTypeOptions);
      })
      .then(() => {
        getAllEvents().then((eventResponse) => {
          let newEventOptions: { text: string; value: string }[] = [];
          let newYearOptions: { text: string; value: string }[] = [];
          eventResponse?.data?.result?.map((eventItem: any) => {
            newEventOptions.push({
              text: eventItem.name,
              value: eventItem.name,
            });
            newYearOptions.push({
              text: new Date(eventItem.dateFrom).getFullYear().toString(),
              value: new Date(eventItem.dateFrom).getFullYear().toString(),
            });
          });
          setAllEventOptions(newEventOptions);
          newYearOptions = newYearOptions.filter(
            (
              value,
              index,
              self //remove duplicate
            ) => index === self.findIndex((t) => t.text === value.text)
          );
          setYearOptions(newYearOptions);
        });
      })
      .finally(() => setActiveSpinner(false))
      .catch((err) => console.log(err));
  };

  type DataIndex = keyof Participant;
  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<Participant> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handleSearch(selectedKeys as string[], confirm, dataIndex);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, border: "none" }}
          >
            Pretraži
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              close();
              setSearchedColumn("");
            }}
            size="small"
            style={{ width: 90 }}
          >
            Poništi
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#C7363D" : undefined }} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex] ?? "")
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
            color: "#0D4077",
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: ColumnsType<Participant | any> = [
    {
      title: "Red. broj",
      width: 100,
      dataIndex: "firstName",
      key: "firstName",
      render: (firstName, text, index) => {
        return (
          <span className="blue-text">
            {(pageNumber - 1) * paginationNumber + index + 1}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "registration",
      key: "registration",
      filters: statusOptions,
      onFilter: (value: any, record: any) =>
        record?.registration?.status === value,
      render: (text, record) => (
        <span className="blue-text name-container">
          <span
            className={
              record?.registration?.status === RegistrationStatus.Pending
                ? "status-dot status-dot-pending"
                : record?.registration?.status === RegistrationStatus.Rejected
                ? "status-dot status-dot-rejected"
                : record?.registration?.status === RegistrationStatus.Approved
                ? "status-dot status-dot-approved"
                : record?.registration?.status === RegistrationStatus.Reserved
                ? "status-dot status-dot-reserved"
                : record?.registration?.status === RegistrationStatus.Paid
                ? "status-dot status-dot-paid"
                : ""
            }
          >
            _
          </span>
          {record?.registration?.status}
        </span>
      ),
    },
    {
      title: "Ime",
      dataIndex: "firstName",
      key: "firstName",
      sorter: (a, b) => (a.firstName < b.firstName ? -1 : 1),
      defaultSortOrder: "ascend",
      render: (firstName, record) => (
        <span className="blue-text name-container">{firstName}</span>
      ),
      ...getColumnSearchProps("firstName"),
    },
    {
      title: "Prezime",
      dataIndex: "lastName",
      key: "lastName",
      sorter: (a, b) => (a.lastName < b.lastName ? -1 : 1),
      render: (lastName) => <span className="blue-text">{lastName}</span>,
      ...getColumnSearchProps("lastName"),
    },
    {
      title: "Događaj",
      dataIndex: "event",
      key: "event",
      sorter: (a, b) =>
        (a?.event?.name ?? "") < (b?.event?.name ?? "") ? -1 : 1,
      filters: allEventOptions, //@ts-ignore
      onFilter: (value: string, record) => record?.event?.name == value,
      render: (event) => <span className="blue-text">{event?.name}</span>,
    },
    {
      title: "Tip učesnika",
      dataIndex: "type",
      key: "type",
      sorter: (a, b) => ((a?.type ?? "") < (b?.type ?? "") ? -1 : 1),
      filters: typeOptions, //@ts-ignore
      onFilter: (value: string, record) => record?.type == value,
      render: (type) => (
        <span className="blue-text">
          {type == "Nepoznat" || type == null || !type ? "/" : type}
        </span>
      ),
    },
    {
      title: "Pravno lice",
      dataIndex: "companyName",
      key: "companyName",
      sorter: (a, b) =>
        (a?.companyName ?? "") < (b?.companyName ?? "") ? -1 : 1,
      render: (companyName) => (
        <span className="blue-text">{companyName ?? "/"}</span>
      ),
      ...getColumnSearchProps("companyName"),
    },
    {
      title: "Telefon",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => <span className="blue-text">{phone}</span>,
      ...getColumnSearchProps("phone"),
    },
    {
      title: "Opcije",
      fixed: "right",
      width: 240,
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <span
            className="action-button"
            onClick={() => handleViewButton(record.id as string)}
          >
            Izmena/Pregled
          </span>
          {(activeUser?.role.name === RoleType.Admin ||
            activeUser?.role.name === RoleType.Employee) && (
            <Popconfirm
              title="Brisanje učesnika?"
              description="Da li ste sigurni da želite da izbrišete učesnika?"
              onConfirm={() => {
                setActiveSpinner(true);
                deleteParticipant(deleteId)
                  .then(() => {
                    toast?.current?.show({
                      severity: "success",
                      detail: "Uspešno izbrisan učesnik",
                      life: 3000,
                    });
                    fetchAllParticipants();
                  })
                  .finally(() => setActiveSpinner(false))
                  .catch((err: any) => console.log(err));
              }}
              onCancel={() => {}}
              okText="Da"
              cancelText="Ne"
            >
              <span
                className="action-button"
                onClick={() => setDeleteId(record.id as string)}
              >
                Brisanje
              </span>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex | any
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const handleViewButton = (participantId: any) => {
    return navigate(`/participant-overview/${participantId}`);
  };

  const handleOpenExportButtons = () => {
    setOpenExportButtons(!openExportButtons);
  };

  const handleEmailButton = () => {
    //TODO...
    alert("Click email button...");
  };

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getParticipantListReportTemplate(filteredParticipantList);
    setPdfData(newPdf);
  }, [filteredParticipantList]);

  const handleExcelButton = () => {
    const filterObject = filteredParticipantList.map((el: any) => {
      return {
        Ime: el.firstName,
        Prezime: el.lastName,
        Dogadjaj: el.event.name,
        Tip: el.type,
        Kompanija: el.companyName ?? "/",
        Telefon: el.phone,
      };
    });

    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(filterObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      saveAsExcelFile(excelBuffer, "Lista učesnika pravnih lica ");
    });
    setOpenExportButtons(false);
  };

  const saveAsExcelFile = (buffer: any, fileName: string) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(
          data,
          fileName +
            "_" +
            formatDate(new Date()) +
            "_" +
            formatTime(new Date()) +
            EXCEL_EXTENSION
        );
      }
    });
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="participant-company-list">
        <Spin spinning={activeSpinner}>
          <Table
            sticky={true}
            scroll={{ x: 1500 }}
            columns={columns}
            pagination={{
              onChange(current) {
                setPageNumber(current);
              },
              defaultPageSize: 30,
              showSizeChanger: true,
              pageSizeOptions: ["30", "50", "100"],
            }}
            dataSource={companyParticipantList}
            onChange={(pagination, filters, sorter, extra) => {
              setPaginationNumber(pagination.pageSize);
              setFilteredParticipantList(extra.currentDataSource);
            }}
            className="table-container"
          />
          <div className="btn-container footer-btn-container">
            {/* <Button
              type="primary"
              onClick={handleEmailButton}
              className="filter-button"
              size="small"
            >
              E-mail obaveštenje
            </Button> */}

            <CustomButton
              danger={false}
              htmlType={"submit"}
              type={"primary"}
              function={() => {
                handleOpenExportButtons();
              }}
              text={"Export"}
            />
          </div>
          {openExportButtons ? (
            <div className="btn-container footer-btn-container">
              <CustomButton
                danger={false}
                htmlType={"submit"}
                type={"primary"}
                function={() => {
                  handleExcelButton();
                }}
                text={"Excel"}
              />
              <CustomButton
                danger={false}
                htmlType={"submit"}
                type={"primary"}
                function={() => {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                }}
                text={"PDF"}
              />
              <CustomButton
                danger={false}
                htmlType={"submit"}
                type={"primary"}
                function={() => {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                }}
                text={"Štampa"}
              />
            </div>
          ) : null}
          <div className="document-preview">
            <PdfPreview
              pdfDefinition={pdfData}
              downloadDocument={downloadPdfDocument}
              printDocument={printPdfDocument}
              documentName={`Lista_ucesnika_pravnih_lica_${
                formatDate(new Date()) + "_" + formatTime(new Date())
              }.pdf`}
            />
          </div>
        </Spin>
      </div>
    </PageLayout>
  );
};

export default ParticipantCompanyListPage;
